import { browserCapabilityService } from "@/features/audio/adapters/browser/browser-capability.service";
import { AudioDeviceService } from "@/features/audio/interfaces/browser-services.interface";
import { AudioDevice, AudioDeviceKind, ResolvedAudioDeviceSelection } from "@/features/audio/types/audio-device.types";
import {
    detectAudioDeviceListDiff,
    enumerateAudioDevices,
    getDeviceLabel,
    isAudioDeviceKind,
    persistResolvedAudioDevices,
    resolveStoredAudioDevices,
} from "@/utils/audio-device-storage.utils";

const mapMediaDevice = (device: MediaDeviceInfo): AudioDevice => {
    if (!isAudioDeviceKind(device.kind)) {
        throw new Error(`Unsupported media device kind: ${device.kind}`);
    }

    return {
        id: device.deviceId,
        label: device.label,
        kind: device.kind,
        isDefault: device.deviceId === "default" || device.label.toLowerCase().includes("default"),
    };
};

const mapMediaDevices = (devices: MediaDeviceInfo[]): AudioDevice[] =>
    devices.filter((device) => device.kind === "audioinput" || device.kind === "audiooutput").map(mapMediaDevice);

export class BrowserAudioDeviceService implements AudioDeviceService {
    async enumerateDevices(): Promise<AudioDevice[]> {
        const devices = await enumerateAudioDevices();
        return mapMediaDevices(devices);
    }

    async resolveStoredDevices(options?: {
        persistResolved?: boolean;
        unnamedLabel?: string;
    }): Promise<ResolvedAudioDeviceSelection & { devices: AudioDevice[] }> {
        const { canSelectSpeaker } = browserCapabilityService.detect();
        const result = await resolveStoredAudioDevices({
            ...options,
            requireOutputDevice: canSelectSpeaker,
        });
        return {
            inputDeviceId: result.inputDeviceId,
            outputDeviceId: result.outputDeviceId,
            isComplete: result.isComplete,
            autoSwitches: result.autoSwitches,
            devices: mapMediaDevices(result.devices),
        };
    }

    persistSelection(selection: ResolvedAudioDeviceSelection): void {
        persistResolvedAudioDevices(selection);
    }

    detectListDiff(previous: AudioDevice[] | null, current: AudioDevice[]) {
        const toMediaInfo = (device: AudioDevice): MediaDeviceInfo =>
            ({
                deviceId: device.id,
                kind: device.kind,
                label: device.label,
                groupId: "",
                toJSON: () => ({}),
            }) as MediaDeviceInfo;

        const diff = detectAudioDeviceListDiff(previous?.map(toMediaInfo) ?? null, current.map(toMediaInfo));

        return {
            added: mapMediaDevices(diff.added),
            removed: mapMediaDevices(diff.removed),
        };
    }

    subscribeToDeviceChanges(callback: () => void): () => void {
        navigator.mediaDevices.addEventListener("devicechange", callback);
        return () => navigator.mediaDevices.removeEventListener("devicechange", callback);
    }

    getDeviceLabel(devices: AudioDevice[], deviceId: string, kind: AudioDeviceKind, unnamedLabel: string): string {
        return getDeviceLabel(
            devices.map(
                (device) =>
                    ({
                        deviceId: device.id,
                        kind: device.kind,
                        label: device.label,
                        groupId: "",
                        toJSON: () => ({}),
                    }) as MediaDeviceInfo
            ),
            deviceId,
            kind,
            unnamedLabel
        );
    }
}

export const browserAudioDeviceService = new BrowserAudioDeviceService();
